import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: 'destructive' | 'warning' | 'primary';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  variant = 'destructive'
}) => {
  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'destructive':
        return {
          icon: 'text-destructive',
          confirmButton: 'bg-destructive hover:bg-destructive/90 focus-visible:outline-destructive'
        };
      case 'warning':
        return {
          icon: 'text-warning',
          confirmButton: 'bg-warning hover:bg-warning/90 focus-visible:outline-warning'
        };
      case 'primary':
        return {
          icon: 'text-primary',
          confirmButton: 'bg-primary hover:bg-primary/90 focus-visible:outline-primary'
        };
      default:
        return {
          icon: 'text-destructive',
          confirmButton: 'bg-destructive hover:bg-destructive/90 focus-visible:outline-destructive'
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Dialog */}
        <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg border border-border">
          <div className="bg-card px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-muted sm:mx-0 sm:h-10 sm:w-10`}>
                <ExclamationTriangleIcon className={`h-6 w-6 ${styles.icon}`} aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3 className="text-base font-semibold leading-6 text-foreground">
                  {title}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {message}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-muted px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <Button
              variant={variant === 'destructive' ? 'destructive' : variant === 'warning' ? 'warning' : 'primary'}
              size="sm"
              onClick={onConfirm}
              disabled={isLoading}
              loading={isLoading}
              className="w-full sm:w-auto sm:ml-3"
            >
              {confirmText}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isLoading}
              className="w-full sm:w-auto mt-3 sm:mt-0"
            >
              {cancelText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;